import { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Search,
  Clock,
  Grid,
  List,
  ArrowLeft,
  CheckCircle
} from "lucide-react";
import { Button } from "../components/ui/button";

// Sample AI services data for demo
const sampleServices = [
  {
    id: 1,
    name: "ChatBot Pro",
    tagline: "24/7 AI Customer Support",
    category: "Customer Support",
    price: "$49/mo",
    priceValue: 49,
    setupTime: "1 day",
    highlight: "Multi-channel",
    features: ["Multi-channel support", "Custom workflows", "Analytics dashboard"],
    companySizes: ["Solo", "2-10", "11-50", "51-200"]
  },
  {
    id: 2,
    name: "LeadGenius AI",
    tagline: "Smart Sales Automation",
    category: "Sales & CRM",
    price: "$149/mo",
    priceValue: 149,
    setupTime: "3 days",
    highlight: "CRM Integration",
    features: ["Lead scoring", "Pipeline automation", "CRM integration"],
    companySizes: ["11-50", "51-200", "201-1000"]
  },
  {
    id: 3,
    name: "ContentMaster",
    tagline: "AI Content Generator",
    category: "Content Creation",
    price: "$79/mo",
    priceValue: 79,
    setupTime: "Same day",
    highlight: "SEO Optimized",
    features: ["Blog writing", "Social media", "SEO optimization"],
    companySizes: ["Solo", "2-10", "11-50"]
  },
  {
    id: 4,
    name: "DataVision Pro",
    tagline: "Predictive Business Analytics",
    category: "Data Analytics",
    price: "$199/mo",
    priceValue: 199,
    setupTime: "1 week",
    highlight: "Real-time",
    features: ["Predictive modeling", "Custom dashboards", "Real-time alerts"],
    companySizes: ["51-200", "201-1000", "1000+"]
  },
  {
    id: 5,
    name: "HireBot AI",
    tagline: "Automated Recruiting Assistant",
    category: "HR & Recruiting",
    price: "$129/mo",
    priceValue: 129,
    setupTime: "2 days",
    highlight: "ATS Integration",
    features: ["Resume screening", "Interview scheduling", "Candidate matching"],
    companySizes: ["11-50", "51-200", "201-1000"]
  },
  {
    id: 6,
    name: "MarketMind",
    tagline: "AI Marketing Automation",
    category: "Marketing",
    price: "$99/mo",
    priceValue: 99,
    setupTime: "Same day",
    highlight: "Email + SMS",
    features: ["Email campaigns", "A/B testing", "Customer segmentation"],
    companySizes: ["Solo", "2-10", "11-50", "51-200"]
  },
  {
    id: 7,
    name: "FinanceAI Pro",
    tagline: "Smart Financial Management",
    category: "Finance",
    price: "$249/mo",
    priceValue: 249,
    setupTime: "1 week",
    highlight: "Enterprise-ready",
    features: ["Expense tracking", "Forecasting", "Invoice automation"],
    companySizes: ["11-50", "51-200", "201-1000", "1000+"]
  },
  {
    id: 8,
    name: "WorkflowBot",
    tagline: "Process Automation Engine",
    category: "Operations",
    price: "$89/mo",
    priceValue: 89,
    setupTime: "3 days",
    highlight: "No-code",
    features: ["Custom workflows", "Task automation", "Integration hub"],
    companySizes: ["2-10", "11-50", "51-200"]
  }
];

const categories = [
  "All Categories",
  "Customer Support",
  "Sales & CRM",
  "Marketing",
  "Data Analytics",
  "Content Creation",
  "HR & Recruiting",
  "Finance",
  "Operations"
];

const priceRanges = [
  { label: "All Prices", min: 0, max: Infinity },
  { label: "Under $50/mo", min: 0, max: 50 },
  { label: "$50-$100/mo", min: 50, max: 100 },
  { label: "$100-$200/mo", min: 100, max: 200 },
  { label: "$200+/mo", min: 200, max: Infinity }
];

const BrowsePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialCategory = location.state?.category || "All Categories";

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedPrice, setSelectedPrice] = useState(priceRanges[0]);
  const [sortBy, setSortBy] = useState("popular");
  const [viewMode, setViewMode] = useState("grid");

  // Filter and sort services
  const filteredServices = useMemo(() => {
    let result = [...sampleServices];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(query) ||
          s.tagline.toLowerCase().includes(query) ||
          s.category.toLowerCase().includes(query)
      );
    }

    // Filter by category
    if (selectedCategory !== "All Categories") {
      result = result.filter((s) => s.category === selectedCategory);
    }

    // Filter by price
    result = result.filter(
      (s) => s.priceValue >= selectedPrice.min && s.priceValue <= selectedPrice.max
    );

    // Sort
    switch (sortBy) {
      case "popular":
        result.sort((a, b) => b.bookedThisWeek - a.bookedThisWeek);
        break;
      case "rating":
        result.sort((a, b) => b.rating - a.rating);
        break;
      case "price-low":
        result.sort((a, b) => a.priceValue - b.priceValue);
        break;
      case "price-high":
        result.sort((a, b) => b.priceValue - a.priceValue);
        break;
      case "newest":
        result.sort((a, b) => b.id - a.id);
        break;
      default:
        break;
    }

    return result;
  }, [searchQuery, selectedCategory, selectedPrice, sortBy]);

  const handleServiceClick = (service) => {
    navigate('/results', { 
      state: { 
        query: service.name,
        selectedService: service,
        fromAssessment: false 
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#121212]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#121212]/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex justify-between items-center h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft size={20} />
            <img src="/logo.svg" alt="Logo" className="w-8 h-8" />
            <span className="text-xl font-bold">AI Fit Engine</span>
          </button>
          <Button
            onClick={() => navigate('/list-product')}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            List Your AI
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          
          {/* Page Header */}
          <div className="py-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Browse AI Services
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Discover 150+ AI solutions for every business need
            </p>
          </div>

          {/* Filters Bar */}
          <div className="bg-white dark:bg-[#1E1E1E] rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-4 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search AI services..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-[#262626] border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-gray-900 dark:text-gray-100"
                />
              </div>

              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2.5 bg-gray-50 dark:bg-[#262626] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-orange-500"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>

              {/* Price Filter */}
              <select
                value={selectedPrice.label}
                onChange={(e) => setSelectedPrice(priceRanges.find((p) => p.label === e.target.value))}
                className="px-4 py-2.5 bg-gray-50 dark:bg-[#262626] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-orange-500"
              >
                {priceRanges.map((range) => (
                  <option key={range.label} value={range.label}>{range.label}</option>
                ))}
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2.5 bg-gray-50 dark:bg-[#262626] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="popular">Most Popular</option>
                <option value="rating">Highest Rated</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="newest">Newest</option>
              </select>

              {/* View Toggle */}
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#262626] rounded-lg p-1">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded-md transition-colors ${viewMode === "grid" ? "bg-white dark:bg-[#1E1E1E] shadow-sm" : ""}`}
                >
                  <Grid size={18} className={viewMode === "grid" ? "text-orange-500" : "text-gray-500"} />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded-md transition-colors ${viewMode === "list" ? "bg-white dark:bg-[#1E1E1E] shadow-sm" : ""}`}
                >
                  <List size={18} className={viewMode === "list" ? "text-orange-500" : "text-gray-500"} />
                </button>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600 dark:text-gray-400">
              Showing <span className="font-semibold text-gray-900 dark:text-gray-100">{filteredServices.length}</span> AI services
              {selectedCategory !== "All Categories" && (
                <span> in <span className="font-semibold text-orange-500">{selectedCategory}</span></span>
              )}
            </p>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-1 rounded-full text-xs">Demo Mode</span>
            </div>
          </div>

          {/* Services Grid/List */}
          {viewMode === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredServices.map((service) => (
                <ServiceCard key={service.id} service={service} onClick={() => handleServiceClick(service)} />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredServices.map((service) => (
                <ServiceListItem key={service.id} service={service} onClick={() => handleServiceClick(service)} />
              ))}
            </div>
          )}

          {/* No Results */}
          {filteredServices.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="text-gray-400" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No services found</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">Try adjusting your filters or search terms</p>
              <Button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("All Categories");
                  setSelectedPrice(priceRanges[0]);
                }}
                variant="outline"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Service Card Component (Grid View)
const ServiceCard = ({ service, onClick }) => (
  <div
    onClick={onClick}
    className="group bg-white dark:bg-[#1E1E1E] rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer border border-gray-100 dark:border-gray-800"
  >
    {/* Header */}
    <div className="p-5">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
          {service.name.charAt(0)}
        </div>
        <div>
          <h3 className="font-bold text-gray-900 dark:text-gray-100 group-hover:text-orange-500 transition-colors">
            {service.name}
          </h3>
          <p className="text-sm text-gray-500">{service.category}</p>
        </div>
      </div>
      
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        {service.tagline}
      </p>
      
      {/* Tags */}
      <div className="flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full">
          <Clock size={10} />
          {service.setupTime}
        </span>
        <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-2 py-1 rounded-full">
          {service.highlight}
        </span>
      </div>
    </div>

    {/* Price Footer */}
    <div className="px-5 py-3 bg-gray-50 dark:bg-[#262626] border-t border-gray-100 dark:border-gray-700">
      <span className="font-semibold text-gray-900 dark:text-gray-100">{service.price}</span>
    </div>
  </div>
);

// Service List Item Component (List View)
const ServiceListItem = ({ service, onClick }) => (
  <div
    onClick={onClick}
    className="group bg-white dark:bg-[#1E1E1E] rounded-xl p-6 shadow-sm hover:shadow-lg transition-all cursor-pointer border border-gray-100 dark:border-gray-800"
  >
    <div className="flex flex-col sm:flex-row gap-4">
      {/* Logo */}
      <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
        {service.name.charAt(0)}
      </div>

      {/* Main Info */}
      <div className="flex-1">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100 group-hover:text-orange-500 transition-colors mb-1">
              {service.name}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">{service.tagline}</p>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{service.price}</p>
          </div>
        </div>

        {/* Info Row */}
        <div className="flex items-center gap-4 mt-3">
          <span className="inline-flex items-center gap-1 text-sm text-gray-500">
            <Clock size={14} />
            {service.setupTime} setup
          </span>
          <span className="text-sm bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full">
            {service.highlight}
          </span>
        </div>

        {/* Features */}
        <div className="flex gap-2 mt-3 flex-wrap">
          {service.features.map((feature, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full">
              <CheckCircle size={12} className="text-green-500" />
              {feature}
            </span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default BrowsePage;
